import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const quantizationComponent: ComponentDef = {
  id: 'quantization',
  name: 'Quantization',
  description: 'Weight/activation quantization strategies: PTQ, QAT, GPTQ, AWQ, SmoothQuant, and FP8.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'ptq',
      options: [
        { label: 'PTQ (Post-Training)', value: 'ptq' },
        { label: 'QAT (Quant-Aware Training)', value: 'qat' },
        { label: 'GPTQ (Hessian-guided)', value: 'gptq' },
        { label: 'AWQ (Activation-Aware)', value: 'awq' },
        { label: 'SmoothQuant (migrate outliers W→A)', value: 'smoothquant' },
        { label: 'FP8 (E4M3/E5M2, H100)', value: 'fp8' },
        { label: 'Absmax (Symmetric)', value: 'absmax' },
        { label: 'Zero-Point (Asymmetric)', value: 'zeropoint' },
        { label: 'Per-Channel', value: 'per_channel' }
      ]
    },
    { name: 'bits', label: 'Bits', type: 'number', default: 8, min: 1, max: 16, step: 1 }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const bits = Number(params.bits);
    const variant = String(params.variant || 'ptq');

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'ptq') {
      // PTQ: collect calibration stats → compute scale/zero-point → quantize weights offline
      g.activation('calib_data', 'x', 'Calibration Data');
      g.activation('stats', 'calib_data', 'min/max or percentile');
      g.linear('w_fp32', 'x', [d_model, d_model]);
      g.scale('scale_zp', 'stats', 'scale = (max−min)/(2^b−1)');
      g.quantize('w_int', 'w_fp32', [d_model, d_model], bits);
      g.dequantize('w_dq', 'w_int', [d_model, d_model]);
      g.matmul('out', 'x', 'w_dq', [seq_len, d_model]);
      g.output('out', 'out');

    } else if (variant === 'qat') {
      // QAT: fake-quant nodes inserted during training; gradients flow through STE
      g.linear('w_fp32', 'x', [d_model, d_model]);
      // Fake-quant: quantize then immediately dequantize, keeping fp32 for gradients
      g.quantize('fake_q', 'w_fp32', [d_model, d_model], bits);
      g.dequantize('fake_dq', 'fake_q', [d_model, d_model]);
      g.matmul('forward_out', 'x', 'fake_dq', [seq_len, d_model]);
      // STE: straight-through estimator passes gradients as if quant didn't exist
      g.activation('ste_grad', 'forward_out', 'STE (∂L/∂W_fp ≈ ∂L/∂W_q)');
      g.output('out', 'ste_grad');

    } else if (variant === 'gptq') {
      // GPTQ: second-order Hessian guides which weights to quantize first, minimizing output error
      g.activation('calib_acts', 'x', 'Calibration Activations');
      g.activation('hessian', 'calib_acts', 'H = 2·X·Xᵀ  (Hessian approx)');
      g.linear('w_fp32', 'x', [d_model, d_model]);
      // Layer-by-layer: quantize column i, update remaining columns to compensate
      g.scale('col_order', 'hessian', 'Cholesky → column order');
      g.quantize('w_int', 'w_fp32', [d_model, d_model], bits);
      g.scale('w_compensated', 'w_int', 'δW = −q_err · H⁻¹_ii · H_i,i+1:');
      g.dequantize('w_dq', 'w_compensated', [d_model, d_model]);
      g.matmul('out', 'x', 'w_dq', [seq_len, d_model]);
      g.output('out', 'out');

    } else if (variant === 'smoothquant') {
      // SmoothQuant: shift quantization difficulty from activations to weights via per-channel scale
      g.activation('act_outliers', 'x', 'Activation outliers (large per-channel variance)');
      // Per-channel migration: s_j = max|x_j|^α / max|w_j|^(1-α)
      g.scale('migrate_scale', 'act_outliers', 's = max|x|^α / max|w|^(1−α)');
      // Scale activations down by s (easier to quantize)
      g.scale('x_smooth', 'x', 'x̃ = x / s  (smooth activations)');
      g.linear('w_fp32', 'x_smooth', [d_model, d_model]);
      // Scale weights up by s (absorb the migration)
      g.scale('w_smooth', 'w_fp32', 'W̃ = W · s  (harder weights)');
      g.quantize('w_int', 'w_smooth', [d_model, d_model], bits);
      g.quantize('x_int', 'x_smooth', [seq_len, d_model], bits);
      g.dequantize('w_dq', 'w_int', [d_model, d_model]);
      g.dequantize('x_dq', 'x_int', [seq_len, d_model]);
      g.matmul('out', 'x_dq', 'w_dq', [seq_len, d_model]);
      g.output('out', 'out');

    } else if (variant === 'fp8') {
      // FP8: hardware-native float8 on H100/H200; two formats E4M3 (weights) and E5M2 (gradients)
      g.linear('w_bf16', 'x', [d_model, d_model]);
      // Per-tensor amax tracking for dynamic scaling
      g.activation('amax_w', 'w_bf16', 'amax(|W|)  — per-tensor absmax');
      g.activation('amax_x', 'x', 'amax(|X|)  — per-tensor absmax');
      g.scale('scale_w', 'amax_w', 'scale_w = 448 / amax_w');
      g.scale('scale_x', 'amax_x', 'scale_x = 448 / amax_x');
      // Cast to FP8 E4M3 (range ±448, 8 mantissa vals)
      g.quantize('w_fp8', 'w_bf16', [d_model, d_model], 8);
      g.quantize('x_fp8', 'x', [seq_len, d_model], 8);
      // FP8 GEMM on tensor core (H100 WGMMA)
      g.matmul('gemm_fp8', 'x_fp8', 'w_fp8', [seq_len, d_model]);
      // Dequantize output to BF16 for residual addition
      g.dequantize('out_bf16', 'gemm_fp8', [seq_len, d_model]);
      g.output('out', 'out_bf16');

    } else if (variant === 'absmax') {
      // Absmax (Symmetric): scale = max(|x|) / 127, quantize x_q = round(x/scale), dequantize x_hat = x_q * scale
      g.linear('w_fp32', 'x', [d_model, d_model]);
      // Compute symmetric scale from absolute maximum
      g.activation('abs_max_w', 'w_fp32', 'max(|W|)  — per-tensor absmax');
      g.scale('scale_w', 'abs_max_w', 'scale = max|W| / (2^(b-1)−1)');
      // Symmetric quantization: x_q = round(x / scale)
      g.quantize('w_int', 'w_fp32', [d_model, d_model], bits);
      // Dequantize: x_hat = x_q * scale
      g.dequantize('w_dq', 'w_int', [d_model, d_model]);
      g.matmul('out', 'x', 'w_dq', [seq_len, d_model]);
      g.output('out', 'out');

    } else if (variant === 'zeropoint') {
      // Zero-Point (Asymmetric): scale = (max-min)/255, zero_point = round(-min/scale)
      g.linear('w_fp32', 'x', [d_model, d_model]);
      // Compute min/max for asymmetric range
      g.activation('w_min', 'w_fp32', 'min(W)');
      g.activation('w_max', 'w_fp32', 'max(W)');
      // Asymmetric scale and zero point
      g.scale('scale_w', 'w_max', 'scale = (max−min) / (2^b−1)');
      g.scale('zero_point', 'w_min', 'zp = round(−min / scale)');
      // Quantize with zero point offset: x_q = round(x/scale) + zp
      g.quantize('w_int', 'w_fp32', [d_model, d_model], bits);
      // Dequantize: x_hat = (x_q - zp) * scale
      g.dequantize('w_dq', 'w_int', [d_model, d_model]);
      g.matmul('out', 'x', 'w_dq', [seq_len, d_model]);
      g.output('out', 'out');

    } else if (variant === 'per_channel') {
      // Per-Channel: same as absmax but compute scale per output channel (row-wise quantization)
      g.linear('w_fp32', 'x', [d_model, d_model]);
      // Per-channel (row-wise) absmax: one scale per output channel
      g.activation('channel_absmax', 'w_fp32', 'max(|W_ij|) per channel i');
      g.scale('scale_per_ch', 'channel_absmax', 'scale_i = max|W_i| / (2^(b-1)−1)');
      // Quantize each channel with its own scale
      g.quantize('w_int', 'w_fp32', [d_model, d_model], bits);
      // Dequantize with per-channel scales
      g.dequantize('w_dq', 'w_int', [d_model, d_model]);
      g.matmul('out', 'x', 'w_dq', [seq_len, d_model]);
      g.output('out', 'out');

    } else {
      // AWQ: find per-channel activation scales s* that minimize quant error of W·diag(s)
      g.activation('act_stats', 'x', 'Activation magnitude (per channel)');
      g.scale('act_scale', 'act_stats', 's* = argmin |Q(W·s)·s⁻¹·x − Wx|');
      g.linear('w_fp32', 'x', [d_model, d_model]);
      // Scale weights by s* before quantizing; scale activations by 1/s* at runtime
      g.mul('w_scaled', 'w_fp32', 'act_scale');
      g.quantize('w_int', 'w_scaled', [d_model, d_model], bits);
      g.dequantize('w_dq', 'w_int', [d_model, d_model]);
      // Activation scaling (absorbed into previous layer in practice)
      g.scale('x_scaled', 'x', '÷ s*');
      g.matmul('out', 'x_scaled', 'w_dq', [seq_len, d_model]);
      g.output('out', 'out');
    }

    return g.build();
  }
};
